// @ds/connected/sharedSections.Bodies — the internal fetching body components
// mounted by the `sharedSections` factories.
//
// Split out of `sharedSections.tsx` so that module (which exports the section
// factories — plain functions) holds no component.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useList } from "@refinedev/core";

import type { Schema } from "@ds/config/schema";
import { useRunListQuery } from "@ds/config/runQuery";
import { auditLogSchema, type AuditLogRow } from "@ds/data/auditLogSchema";
import {
  deriveChanges,
  formatChangeValue,
  inferKind,
  partitionByImportance,
  type AuditActivityKind,
  type DerivedChange,
} from "@ds/data/auditChanges";
import {
  mergeScopedAuditRows,
  selectChildAuditRows,
  type AnyAuditChildScope,
  type ChildAuditLogRow,
  type ScopedAuditRow,
} from "@ds/data/auditChildScope";
import * as Sentry from "@sentry/react";
import type { AuditAction } from "@shared/types/audit";
import { Switch } from "@ds/editable/Switch";

import { Box } from "@ds/layout/Box";
import { Grid } from "@ds/layout/Grid";
import { Inline } from "@ds/layout/Inline";
import { Stack } from "@ds/layout/Stack";
import { Alert } from "antd";
import { Eyebrow } from "@ds/display/Eyebrow";
import { TextValue } from "@ds/display/TextValue";

import {
  ActivityLog,
  type ActivityEntry,
  type ActivityLogGroup,
} from "@ds/activity/ActivityLog";
import { CommentThread as ConnectedCommentThread } from "@ds/connected/CommentThread";
import {
  FileListPills,
  type FileListPillsItem,
} from "@ds/tables/cells/FileListPills";

import { useFormat } from "@ds/display/useNumberFormat";

import type { FileBucket, ExtraFilter } from "@ds/connected/sharedSections";

const ACTION_LABEL_KEY: Record<AuditAction, string> = {
  INSERT: "common.audit.action.insert",
  UPDATE: "common.audit.action.update",
  DELETE: "common.audit.action.delete",
};

const ACTION_LABEL_FALLBACK: Record<AuditAction, string> = {
  INSERT: "Utworzono",
  UPDATE: "Zaktualizowano",
  DELETE: "Usunięto",
};

/** How many changed-field names the one-line summary shows before "+N". */
const SUMMARY_FIELD_LIMIT = 3;

// `AuditLogRow` doesn't carry `entity_id` (auditLogSchema's default select
// omits it — the self-scope filter needs it in the WHERE clause only, never
// the SELECT). The child-audit query below asks for it explicitly via
// `also: ["entity_id"]` so we can map each child audit row back to the
// child record it belongs to, without touching `auditLogSchema.ts`.
// `ChildAuditLogRow` lives in `@ds/data/auditChildScope` — it is part of the
// contract every `childScope.label` callback is handed.

/**
 * How many child tables one activity feed can weave in. Each slot costs two
 * unconditional queries (child records + their audit rows), disabled when the
 * slot is empty — the same fixed-arity trade `useSubQueries` makes.
 */
export const MAX_AUDIT_CHILD_SCOPES = 3;

/** Stable de-duplicated union of two id lists (live children + removed ones). */
function useUnion(a: number[], b: number[]): number[] {
  return useMemo(() => Array.from(new Set([...a, ...b])), [a, b]);
}

/** One slot's child records. `scope: null` keeps the hook mounted but idle. */
function useChildScopeRecords(scope: AnyAuditChildScope | null, entityId: number) {
  const { result } = useList<{ id: number }>({
    resource: scope?.resource ?? "",
    filters: scope ? [{ field: scope.fk, operator: "eq", value: entityId }] : [],
    meta: scope ? { select: scope.select } : undefined,
    pagination: { mode: "off" },
    queryOptions: { enabled: !!scope && entityId > 0 },
  });
  const data = result.data;
  const ids = useMemo(() => data.map((c) => c.id), [data]);
  return useMemo(() => ({ data, ids }), [data, ids]);
}

/**
 * Ids of a lifecycle scope's REMOVED children.
 *
 * The audit query below scopes by child id, and a hard-deleted child is no
 * longer in `scope.resource` — so its rows (the DELETE that records the
 * removal, and any earlier INSERT/UPDATE) would be unreachable. The DELETE row
 * carries a full `before_value` snapshot, so the parent FK is recoverable from
 * the row itself. Idle for scopes that don't opt into `lifecycle`.
 */
function useChildScopeRemovals(scope: AnyAuditChildScope | null, entityId: number) {
  const lifecycle = !!scope?.lifecycle;
  const { result } = useRunListQuery<AuditLogRow>({
    schema: auditLogSchema,
    filter: [
      { field: "entity_type", operator: "eq", value: scope?.entityType ?? "" },
      { field: "action",      operator: "eq", value: "DELETE" },
      // `->>` yields text, so the parent id is compared as a string. No dot in
      // the path, so `detectInnerJoins` leaves the SELECT alone.
      { field: `before_value->>${scope?.fk ?? "id"}`, operator: "eq", value: String(entityId) },
    ],
    sort: [{ field: "id", order: "desc" }],
    page: { size: 100 },
    enabled: lifecycle && entityId > 0,
    also: ["entity_id"],
  });
  const data = result.data as ChildAuditLogRow[];
  return useMemo(() => (lifecycle ? data.map((r) => r.entity_id) : []), [lifecycle, data]);
}

/** One slot's child audit rows, scoped to that slot's `entityType` + ids. */
function useChildScopeAudit(scope: AnyAuditChildScope | null, ids: number[], pageSize: number) {
  const { result } = useRunListQuery<AuditLogRow>({
    schema: auditLogSchema,
    filter: [
      { field: "entity_type", operator: "eq", value: scope?.entityType ?? "" },
      { field: "entity_id",   operator: "in", value: ids },
    ],
    sort: [
      { field: "created_at", order: "desc" },
      { field: "id", order: "desc" },
    ],
    page: { size: pageSize },
    enabled: !!scope && ids.length > 0,
    // `entity_type` joins a merged row back to the scope that admitted it;
    // `entity_id` back to the child record it describes.
    also: ["entity_id", "entity_type"],
  });
  const data = result.data as ChildAuditLogRow[];
  return useMemo(() => ({ data }), [data]);
}

interface PreparedEntry {
  row: AuditLogRow;
  changes: DerivedChange[];
  important: DerivedChange[];
  kind: AuditActivityKind;
  /** Machine UPDATE churn (actor NULL) — hidden behind the system toggle. */
  isSystemUpdate: boolean;
  /** Touches ≥1 important field, or is a milestone / feed without an importance list. */
  isImportant: boolean;
  /** Whether this row is the section's own entity or a woven-in child row. */
  scope: "self" | "child";
  /** The audit `entity_type` this row describes — picks the column-label namespace. */
  auditEntityType: string;
}

interface ActivityBodyProps {
  entityType: string;
  entityId: number;
  pageSize: number;
  testId: string;
  importantFields?: readonly string[];
  /** Weaves child tables' scoped audit rows (verdicts, transport legs) into this feed. */
  childScopes?: readonly AnyAuditChildScope[];
}

export function ActivityBody({
  entityType, entityId, pageSize, testId, importantFields, childScopes,
}: ActivityBodyProps): React.ReactElement {
  const fmt = useFormat();
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const [showSystem, setShowSystem] = useState(false);

  // Single dispatcher call — the entity never touches useRun*Query directly.
  const { query, result } = useRunListQuery<AuditLogRow>({
    schema: auditLogSchema,
    filter: [
      { field: "entity_type", operator: "eq", value: entityType },
      { field: "entity_id",   operator: "eq", value: entityId },
    ],
    sort: [
      { field: "created_at", order: "desc" },
      // Same-second writes (one transaction touching several fields) keep
      // insertion order without this tiebreaker.
      { field: "id", order: "desc" },
    ],
    page: { size: pageSize },
    enabled: entityId > 0,
  });
  const isError = query.isError;
  const rows: AuditLogRow[] = result.data;

  // Pad the scope list to a fixed length so every hook call below is
  // unconditional (React rules) — same shape as `useSubQueries`' slots.
  const paddedScopes = useMemo(() => {
    const out: (AnyAuditChildScope | null)[] = [...(childScopes ?? [])].slice(0, MAX_AUDIT_CHILD_SCOPES);
    while (out.length < MAX_AUDIT_CHILD_SCOPES) out.push(null);
    return out;
  }, [childScopes]);

  // Dev warning: silent truncation past MAX_AUDIT_CHILD_SCOPES is a footgun —
  // the extra table's history would just never appear, with nothing to see.
  if (process.env.NODE_ENV !== "production" && (childScopes?.length ?? 0) > MAX_AUDIT_CHILD_SCOPES) {
    Sentry.captureMessage(
      `[ActivityBody] ${childScopes?.length} child scopes declared on "${entityType}" but ` +
        `MAX_AUDIT_CHILD_SCOPES=${MAX_AUDIT_CHILD_SCOPES}. Scopes at indices ` +
        `${MAX_AUDIT_CHILD_SCOPES}+ are dropped and their history will not appear.`,
      "warning",
    );
  }

  // Child rows themselves (e.g. transaction_files, transaction_transports) —
  // fetched only to resolve labels (`scope.label(row, child, t)`); never
  // rendered on their own. Kept as one query per scope, deliberately, instead
  // of a nested `or`/`and` PostgREST filter — nested-filter syntax through
  // @refinedev/supabase is unproven here and a failure would be silent.
  const c0 = useChildScopeRecords(paddedScopes[0], entityId);
  const c1 = useChildScopeRecords(paddedScopes[1], entityId);
  const c2 = useChildScopeRecords(paddedScopes[2], entityId);

  // Children this parent has lost — their ids survive only in the DELETE
  // snapshot, and a lifecycle feed must still show the removal.
  const r0 = useChildScopeRemovals(paddedScopes[0], entityId);
  const r1 = useChildScopeRemovals(paddedScopes[1], entityId);
  const r2 = useChildScopeRemovals(paddedScopes[2], entityId);

  // The child rows' own audit-log entries, one query per scope's entity_type.
  const a0 = useChildScopeAudit(paddedScopes[0], useUnion(c0.ids, r0), pageSize);
  const a1 = useChildScopeAudit(paddedScopes[1], useUnion(c1.ids, r1), pageSize);
  const a2 = useChildScopeAudit(paddedScopes[2], useUnion(c2.ids, r2), pageSize);

  const slots = useMemo(
    () => [{ records: c0, audit: a0 }, { records: c1, audit: a1 }, { records: c2, audit: a2 }],
    [c0, a0, c1, a1, c2, a2],
  );

  // "<entity_type>:<entity_id>" → the child record, for label resolution. Keyed
  // by BOTH because ids are per-table sequences: file #5 and transport #5 both
  // exist, and a bare id map would hand a scope the wrong table's record.
  const childByKey = useMemo(() => {
    const m = new Map<string, { id: number }>();
    paddedScopes.forEach((scope, i) => {
      if (!scope) return;
      for (const child of slots[i].records.data) m.set(`${scope.entityType}:${child.id}`, child);
    });
    return m;
  }, [paddedScopes, slots]);

  const scopeByEntityType = useMemo(() => {
    const m = new Map<string, AnyAuditChildScope>();
    for (const scope of paddedScopes) if (scope) m.set(scope.entityType, scope);
    return m;
  }, [paddedScopes]);

  const scopedRows = useMemo<ScopedAuditRow[]>(
    () => mergeScopedAuditRows(
      rows,
      paddedScopes.flatMap((scope, i) =>
        scope ? selectChildAuditRows(slots[i].audit.data, scope.fields, { lifecycle: scope.lifecycle }) : [],
      ),
      pageSize,
    ),
    [rows, paddedScopes, slots, pageSize],
  );

  const boolLabels = useMemo(() => ({
    true: t("common.yes", "Tak"),
    false: t("common.no", "Nie"),
  }), [t]);

  // Column labels live per audit entity_type — a child row's fields must be
  // read out of ITS table's namespace, not the parent's.
  const translateField = useCallback((field: string, auditEntityType: string): string => {
    const translated = t(`audit.column_names.${auditEntityType}.${field}`, { defaultValue: "" });
    return translated || field;
  }, [t]);

  const prepared = useMemo<PreparedEntry[]>(() => scopedRows.map(({ row, scope }) => {
    const changes = deriveChanges(row);
    const { important } = partitionByImportance(changes, importantFields);
    return {
      row,
      changes,
      important,
      kind: inferKind(row, changes),
      isSystemUpdate: row.actor_id == null && row.action === "UPDATE",
      isImportant: scope === "child" || row.action !== "UPDATE" || !importantFields?.length || important.length > 0,
      scope,
      // Child rows carry `entity_type` via the slot query's `also`; the
      // parent's own rows are the section's entity by construction.
      auditEntityType: scope === "child" ? (row as ChildAuditLogRow).entity_type : entityType,
    };
  }), [scopedRows, importantFields, entityType]);

  const systemCount = useMemo(
    () => prepared.filter((p) => p.isSystemUpdate).length,
    [prepared],
  );
  const mutedCount = useMemo(
    () => prepared.filter((p) => !p.isSystemUpdate && !p.isImportant).length,
    [prepared],
  );

  const entryLabel = useCallback((p: PreparedEntry): string => {
    if (p.scope === "child") {
      // Child rows come from a slot query with `also: ["entity_id","entity_type"]`,
      // so both columns are present on every row reaching this branch.
      const row = p.row as ChildAuditLogRow;
      const scope = scopeByEntityType.get(row.entity_type);
      if (scope) {
        return scope.label(row, childByKey.get(`${row.entity_type}:${row.entity_id}`), t);
      }
    }
    if (p.row.action !== "UPDATE") {
      return t(ACTION_LABEL_KEY[p.row.action], ACTION_LABEL_FALLBACK[p.row.action]);
    }
    if (p.kind === "deleted") {
      return t(ACTION_LABEL_KEY.DELETE, ACTION_LABEL_FALLBACK.DELETE);
    }
    if (p.kind === "custom") {
      return t("audit.feed.restored", "Przywrócono");
    }
    const source = p.important.length > 0 ? p.important : p.changes;
    if (source.length === 0) {
      return t(ACTION_LABEL_KEY.UPDATE, ACTION_LABEL_FALLBACK.UPDATE);
    }
    const names = source
      .slice(0, SUMMARY_FIELD_LIMIT)
      .map((c) => translateField(c.field, p.auditEntityType))
      .join(", ");
    const rest = source.length - Math.min(source.length, SUMMARY_FIELD_LIMIT);
    const summary = t("audit.feed.changed", "Zmieniono: {{fields}}", { fields: names });
    return rest > 0 ? `${summary} +${rest}` : summary;
  }, [t, translateField, scopeByEntityType, childByKey]);

  const groups = useMemo<ActivityLogGroup[]>(() => {
    const byDay = new Map<string, ActivityEntry[]>();
    for (const p of prepared) {
      if (!showSystem && p.isSystemUpdate) continue;
      if (!showAll && !p.isImportant) continue;
      const r = p.row;
      const key = fmt.date(r.created_at);
      const entry: ActivityEntry = {
        id: r.id,
        at: fmt.time(r.created_at),
        kind: p.kind,
        actor: {
          name: r.actor_name ?? (r.actor_id == null ? t("audit.feed.system", "System") : "—"),
          avatar: r.actor_photo ?? undefined,
        },
        label: entryLabel(p),
        changes: p.changes.length > 0
          ? p.changes.map((c) => ({
              field: translateField(c.field, p.auditEntityType),
              before: c.before == null ? null : formatChangeValue(c.before, boolLabels),
              after: c.after == null ? null : formatChangeValue(c.after, boolLabels),
            }))
          : undefined,
      };
      const arr = byDay.get(key);
      if (arr) arr.push(entry);
      else byDay.set(key, [entry]);
    }
    return Array.from(byDay.entries()).map(([date, entries]) => ({ date, entries }));
  }, [prepared, showAll, showSystem, fmt, t, entryLabel, translateField, boolLabels]);

  if (isError) {
    return (
      <Box paddingX="lg" paddingY="md" fullWidth testId={`${testId}-error`}>
        <Alert type="error" message="Nie udało się załadować danych" />
      </Box>
    );
  }

  const showImportanceToggle = mutedCount > 0 || showAll;
  const showSystemToggle = systemCount > 0 || showSystem;

  return (
    <Stack gap="sm">
      {(showImportanceToggle || showSystemToggle) && (
        <Inline gap="lg" align="center" testId={`${testId}-toggles`}>
          {showImportanceToggle && (
            <Inline gap="xs" align="center" inline>
              <Switch size="small" checked={showAll} onChange={setShowAll} />
              <TextValue tone="muted" size="xs">
                {t("audit.feed.show_all", "Wszystkie zmiany")} ({mutedCount})
              </TextValue>
            </Inline>
          )}
          {showSystemToggle && (
            <Inline gap="xs" align="center" inline>
              <Switch size="small" checked={showSystem} onChange={setShowSystem} />
              <TextValue tone="muted" size="xs">
                {t("audit.feed.show_system", "Zmiany systemowe")} ({systemCount})
              </TextValue>
            </Inline>
          )}
        </Inline>
      )}
      <ActivityLog
        groups={groups}
        emptyText="Brak aktywności"
        testId={testId}
      />
    </Stack>
  );
}


interface CommentsBodyProps {
  commentableType: string;
  commentableId: number;
  testId: string;
}

export function CommentsBody({ commentableType, commentableId, testId }: CommentsBodyProps): React.ReactElement {
  return (
    <ConnectedCommentThread
      commentableType={commentableType as never}
      commentableId={commentableId}
      testId={testId}
    />
  );
}


interface FilesBodyProps<TFile> {
  schema: Schema<TFile>;
  parentFk: string;
  parentId: number;
  extraFilters: ReadonlyArray<ExtraFilter>;
  buckets: FileBucket<TFile>[];
  testId: string;
  pageSize: number;
  onCount?: (n: number | undefined) => void;
}

export function FilesBody<TFile extends { id: number | string }>(
  { schema, parentFk, parentId, extraFilters, buckets, testId, pageSize, onCount }: FilesBodyProps<TFile>,
): React.ReactElement {
  // Dispatcher call — single site for the resource's data lifecycle.
  const { query, result } = useRunListQuery<TFile>({
    schema,
    filter: [
      { field: parentFk, operator: "eq", value: parentId },
      ...extraFilters.map((f) => ({
        field: f.field,
        operator: f.operator,
        value: f.value,
      })),
    ],
    page: { size: pageSize },
    enabled: parentId > 0,
  });
  const isLoading = query.isLoading;
  const isError   = query.isError;
  const files: TFile[] = result.data;

  useEffect(() => {
    onCount?.(isLoading ? undefined : files.length);
  }, [files.length, isLoading, onCount]);

  if (isError) {
    return (
      <Box paddingX="lg" paddingY="md" fullWidth testId={`${testId}-error`}>
        <Alert type="error" message="Nie udało się załadować danych" />
      </Box>
    );
  }

  return (
    <Stack gap="none" divider="soft" testId={testId}>
      {buckets.map((bucket) => {
        const bucketFiles = files.filter(bucket.filter);
        const items: FileListPillsItem[] = bucketFiles.map((f) => ({
          id: String(f.id),
          name: bucket.pillLabel ? bucket.pillLabel(f) : String(f.id),
          tone: bucket.pillTone ? bucket.pillTone(f) : "grey",
        }));
        const byId = new Map(bucketFiles.map((f) => [String(f.id), f]));
        const onUpload = bucket.onUpload;
        return (
          <Box key={bucket.id} size="md" testId={`${testId}-bucket-${bucket.id}`}>
            <Grid columns="label-value" gap="md" align="center">
              <Eyebrow>{bucket.label}</Eyebrow>
              <FileListPills
                files={items}
                accept={bucket.accept}
                max={Infinity}
                onClick={(id) => {
                  const f = byId.get(String(id));
                  if (f && bucket.onClick) bucket.onClick(f);
                }}
                onUpload={onUpload
                  ? (fl) => onUpload(Array.from(fl))
                  : undefined}
              />
            </Grid>
          </Box>
        );
      })}
    </Stack>
  );
}

